import { useState } from 'react'
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  KeyboardAvoidingView,
  Platform,
  TextInput,
  Keyboard,
} from 'react-native'
import ErrorBoundary from 'react-native-error-boundary'

import Icon from 'react-native-vector-icons/Feather'
import { RNChipView } from 'react-native-chip-view'
import Autocomplete from 'react-native-autocomplete-input'
import { i18n } from '$localization/config.js'
import ButtonDone from '$components/button_done'
import ButtonToggle from '$components/button_toggle'
import LoadingView from '$constants/LoadingView.js'
import ModalLoading from '$components/modal_loading'
import FlexContainer from '$components/flex_container'
import MainWrapper from '$components/main_wrapper/MainWrapper.js'
import styles from './styles/styles.main'
import debounce from '$utils/Debounce'

export default function GeneTestUI({
  navigation,
  geneTestFromOnboarding,
  colorSettings,
  disableDoneButton,
  isLoading,
  selectedGeneTest,
  scrollViewHeight,
  loadMainQuestion,
  loadSubQuestion,
  mainQuestionValue,
  loadGeneList,
  handleDeleteSelectedGene,
  subQuestionValue,
  handleMainQuestion,
  handleSubQuestion,
  handleSelectedGeneTest,
  handleGeneTestSaving,
  handleToolTip,
  UMLSGenesList,
  buttonText,
  loadUMLSGenes,
  currentMember,
  clickedMember,
}) {
  const [inputValue, setInputValue] = useState('')

  const loadingScreen = (
    <FlexContainer style={{ height: scrollViewHeight }}>
      <LoadingView
        backgroundColor={colorSettings?.onboarding_bgcolor}
        tintColor={colorSettings?.btn_no_fill_text_2}
        textColor={colorSettings?.btn_no_fill_text_2}
        visible={isLoading}
      />
    </FlexContainer>
  )

  const renderMainQuestion = (
    <ButtonToggle
      title={
        i18n.t('have_you_ever_had_genetic_testing_for_hereditary_cancer')
          .default
      }
      colorSettings={colorSettings}
      onPress={(value) => handleMainQuestion(value)}
      setContainerStyle={{ marginTop: 20, marginBottom: 20 }}
      isSelected={mainQuestionValue}
    />
  )

  const renderSubQuestion = () => {
    const isCurrentUser =
      currentMember?.memberID === clickedMember?.member_id
        ? 'did_you_test_positive_for_any_genes'
        : 'did_this_person_test_positive_for_any_genes'

    return (
      <View>
        <Text
          style={[
            styles.subQuestionTitle,
            {
              color: colorSettings?.text_input_label_color_2,
            },
          ]}
        >
          {i18n.t(isCurrentUser).default}
        </Text>
        <TouchableOpacity style={styles.toolTipButton} onPress={handleToolTip}>
          <Icon
            name="info"
            size={20}
            color={colorSettings?.text_input_label_color_2 || 'black'}
          />
        </TouchableOpacity>
        <ButtonToggle
          colorSettings={colorSettings}
          onPress={(value) => handleSubQuestion(value)}
          setContainerStyle={styles.buttonToggleCustomStyle}
          isSelected={subQuestionValue}
        />
      </View>
    )
  }

  const renderChips = (
    <View style={styles.chipContainer}>
      {selectedGeneTest &&
        selectedGeneTest.map((data) => (
          <View style={styles.chipWrapContainer}>
            <RNChipView
              avatar={false}
              backgroundColor="rgba(41,43,47,0.6)"
              cancelable
              cancelableStyle={{
                backgroundColor: '#AE9AB6',
                color: 'black',
              }}
              title={data.Name}
              titleStyle={styles.chipText}
              onPress={() => handleDeleteSelectedGene(data)}
            />
          </View>
        ))}
    </View>
  )

  const renderAutocomplete = (
    <Autocomplete
      data={UMLSGenesList}
      containerStyle={{
        width: '100%',
        backgroundColor: 'transparent',
      }}
      inputContainerStyle={[
        {
          backgroundColor:
            colorSettings.selectable_btn_border_inactive_1 || 'black',
        },
        styles.autocompleteInputContainer,
      ]}
      listContainerStyle={{
        flex: 1,
      }}
      renderTextInput={() => (
        <>
          <View style={styles.textInputContainerCentered}>
            <TextInput
              style={[
                {
                  color: colorSettings?.text_input_label_color_2 || 'black',
                },
                styles.autocompleteTextInput,
              ]}
              onChangeText={(value) => {
                debounce(loadUMLSGenes(value, ''))
                setInputValue(value)
              }}
              value={inputValue}
              placeholder={`${i18n.t('search_genes').default}: `}
              placeholderTextColor={
                colorSettings?.text_input_label_color_2 || 'black'
              }
              onBlur={Keyboard.dismiss}
            />
            <Icon
              name="search"
              size={20}
              color={colorSettings?.text_input_label_color_2 || 'black'}
            />
          </View>
        </>
      )}
      autoCorrect={false}
      hideResults={false}
      flatListProps={{
        showsVerticalScrollIndicator: false,
        style: styles.autocompleteList,
        keyboardShouldPersistTaps: 'always',
        renderItem: ({ item }) => (
          <TouchableOpacity
            style={[
              {
                backgroundColor:
                  colorSettings.selectable_btn_inactive_1 || 'black',
                borderColor:
                  colorSettings.selectable_btn_border_inactive_1 || 'black',
              },
              styles.autocompleteListButtons,
              item.isSelected && {
                backgroundColor:
                  colorSettings.selectable_btn_active_1 || 'white',
              },
            ]}
            onPress={() => {
              item.CUI === 'more'
                ? debounce(loadUMLSGenes(inputValue, item.CUI))
                : handleSelectedGeneTest(item)
            }}
          >
            <Text
              style={[
                {
                  color: colorSettings?.text_input_label_color_2 || 'black',
                },
                styles.autocompleteListButtonText,
              ]}
            >
              {item.Name}
            </Text>
          </TouchableOpacity>
        ),
      }}
    />
  )

  let _switchView
  if (isLoading) {
    _switchView = loadingScreen
  } else {
    _switchView = (
      <FlexContainer
        style={{ height: scrollViewHeight, justifyContent: 'flex-start' }}
      >
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={styles.container}
        >
          <ScrollView style={styles.container}>
            {loadMainQuestion && renderMainQuestion}
            {loadSubQuestion && renderSubQuestion()}
            {renderChips}
            {loadGeneList && renderAutocomplete}
          </ScrollView>
        </KeyboardAvoidingView>
      </FlexContainer>
    )
  }

  return (
    <ErrorBoundary>
      <MainWrapper
        navigation={navigation}
        disableLogout={geneTestFromOnboarding}
      >
        <SafeAreaView
          style={[
            styles.container,
            {
              backgroundColor: colorSettings?.onboarding_bgcolor || 'white',
            },
          ]}
        >
          {_switchView}
          <ButtonDone
            colorSettings={colorSettings}
            onPress={() => handleGeneTestSaving()}
            title={i18n.t(buttonText).default}
            disable={disableDoneButton}
          />
          <ModalLoading
            colorSettings={colorSettings}
            isVisible={false}
            loadingTitle={i18n.t('saving_datanplease_wait').default}
          />
        </SafeAreaView>
      </MainWrapper>
    </ErrorBoundary>
  )
}
